/**
 * TXC "My bike" garage — the single source of truth for the customer's
 * remembered bike collection choice, shared by the homepage bike finder
 * (sections/shop-by-bike.liquid) and the site-wide "My bike" bar
 * (snippets/my-bike-bar.liquid). Unrelated to the per-product fitment
 * checker (txc-bike-fitment.js) — this module only ever stores a
 * collection HANDLE the customer explicitly chose and submitted, never a
 * guessed title, the current collection, or a product.
 *
 * Storage schema (localStorage key "txc:garage:v1"):
 *   { "v": 1, "handle": "<collection-handle>" }
 * Any other shape, a different "v", or a missing/empty handle is treated
 * as no selection — parsing never throws, callers never crash on it.
 *
 * When localStorage is unavailable or denied (private mode, quota, etc.)
 * the chosen handle still lives in memory for the rest of this page's
 * lifetime, and save()/clear() report persisted: false so callers can show
 * an honest "saved for this visit only" status instead of claiming a
 * permanent save that didn't actually happen.
 */
(function (global) {
  'use strict';

  if (global.TxcGarage) return;

  var STORAGE_KEY = 'txc:garage:v1';
  var STORAGE_VERSION = 1;
  var CHANGE_EVENT = 'txc:garage-changed';

  var memoryHandle = null;
  var memoryOnly = false;

  function isNonEmptyString(value) {
    return typeof value === 'string' && value.length > 0;
  }

  function parseStoredHandle(raw) {
    if (!isNonEmptyString(raw)) return null;
    var data;
    try {
      data = JSON.parse(raw);
    } catch (e) {
      return null;
    }
    if (!data || typeof data !== 'object') return null;
    if (data.v !== STORAGE_VERSION) return null;
    if (!isNonEmptyString(data.handle)) return null;
    return data.handle;
  }

  function getHandle() {
    // A failed write/clear must override an older readable stored choice.
    if (memoryOnly) return memoryHandle;
    try {
      var parsed = parseStoredHandle(global.localStorage.getItem(STORAGE_KEY));
      if (parsed) return parsed;
      return memoryOnly ? memoryHandle : null;
    } catch (e) {
      memoryOnly = true;
      return memoryHandle;
    }
  }

  function dispatchChange(handle) {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handle: handle || null } }));
  }

  function save(handle) {
    if (!isNonEmptyString(handle)) {
      return { ok: false, persisted: false };
    }
    memoryHandle = handle;
    var persisted = true;
    try {
      global.localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: STORAGE_VERSION, handle: handle }));
      memoryOnly = false;
    } catch (e) {
      persisted = false;
      memoryOnly = true;
    }
    dispatchChange(handle);
    return { ok: true, persisted: persisted };
  }

  function clear() {
    memoryHandle = null;
    var persisted = true;
    try {
      global.localStorage.removeItem(STORAGE_KEY);
      memoryOnly = false;
    } catch (e) {
      persisted = false;
      memoryOnly = true;
    }
    dispatchChange(null);
    return { ok: true, persisted: persisted };
  }

  global.TxcGarage = {
    STORAGE_KEY: STORAGE_KEY,
    CHANGE_EVENT: CHANGE_EVENT,
    getHandle: getHandle,
    save: save,
    clear: clear
  };
  global.addEventListener('storage', function (event) {
    if (event.key === STORAGE_KEY || event.key === null) {
      memoryOnly = false;
      memoryHandle = null;
    }
  });
})(window);
